import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { InfiniteQueryOptionsOverride, QueryOptionsOverride } from '../queryOptions.helpers';
import type {
  AvailableRefereeRewardsCountDto,
  UserRefereeListDto,
  UserRefereeParams,
  UserRefereeSort,
  UserRefereeSummaryDto,
  UserReferralDto,
} from './referral.schema';
import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { QueriesKeys } from '../queriesKeys';
import { referralApi } from './referral.api';
import { REFEREES_MAX_LIMIT } from './referral.schema';

export const referralQueryOptions = {
  meReferral: (options?: QueryOptionsOverride<UserReferralDto | null>) => queryOptions({
    queryKey: [QueriesKeys.meReferral],
    queryFn: () => referralApi.getMeReferral(),
    ...options,
  }),

  meReferees: (params?: UserRefereeParams, options?: QueryOptionsOverride<UserRefereeListDto>) => queryOptions({
    queryKey: [QueriesKeys.meReferees, params],
    queryFn: () => referralApi.getMeReferees(params),
    ...options,
  }),

  /**
   * Keeps every loaded page: the backend sorts referees (newest invite first by default) but has no filters,
   * so tabs and "Friend N" numbering are done over the loaded pages.
   */
  meRefereesInfinite: (
    sort?: UserRefereeSort,
    options?: InfiniteQueryOptionsOverride<UserRefereeListDto, Error, InfiniteData<UserRefereeListDto, number>>,
  ) => infiniteQueryOptions({
    queryKey: [QueriesKeys.meReferees, 'infinite', sort],
    queryFn: ({ pageParam }) => referralApi.getMeReferees({ page: pageParam, limit: REFEREES_MAX_LIMIT, sort }),
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    getNextPageParam: data => data.hasNextPage ? data.page + 1 : undefined,
    ...options,
  }),

  meRefereesSummary: (options?: QueryOptionsOverride<UserRefereeSummaryDto>) => queryOptions({
    queryKey: [QueriesKeys.meRefereesSummary],
    queryFn: () => referralApi.getMeRefereesSummary(),
    retry: false,
    ...options,
  }),

  meRefereesAvailableRewardsCount: (options?: QueryOptionsOverride<AvailableRefereeRewardsCountDto>) => queryOptions({
    queryKey: [QueriesKeys.meRefereesAvailableRewardsCount],
    queryFn: () => referralApi.getMeRefereesAvailableRewardsCount(),
    ...options,
  }),
};

export const invalidateRefereeRewardState = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.meReferees] });
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.rewardsGrouped] });
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.meRefereesSummary] });
  queryClient.invalidateQueries({ queryKey: [QueriesKeys.meRefereesAvailableRewardsCount] });
};
